import { useState, useEffect } from 'react';
import { Container, Header, Table, Box, StatusIndicator, Button, SpaceBetween } from '@awsui/components-react';
import { competitiveIntelligenceApi } from '../../services/CompetitiveIntelligenceApi';
import { getUserEmail } from '../../utils/auth';

export function MyResearchComponent() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingUrls, setDownloadingUrls] = useState(new Set());

    useEffect(() => {
        const userEmail = getUserEmail();
        console.log('Fetching jobs for user:', userEmail);

        // Setup WebSocket handler for jobs list
        const unsubscribe = competitiveIntelligenceApi.setupWebSocketHandlers({
            'jobs_list': (message) => {
                console.log('Received jobs list:', message.jobs);
                setJobs(message.jobs || []);
                setLoading(false);
            },
            'download_link': (message) => {
                console.log('Received download link:', message);
                if (message.presigned_url) {
                    window.open(message.presigned_url, '_blank');
                }
                setDownloadingUrls(prev => {
                    const next = new Set(prev);
                    next.delete(message.s3_url);
                    return next;
                });
            },
            'error': (message) => {
                console.error('Error:', message.message);
                setLoading(false);
                setDownloadingUrls(new Set());
            }
        }, userEmail);

        // Request jobs list
        competitiveIntelligenceApi.listJobsWebSocket(userEmail);

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, []);

    const getStatusIndicator = (status, jobId) => {
        switch (status) {
            case 'completed':
                return <StatusIndicator type="success">Completed</StatusIndicator>;
            case 'running':
                return <StatusIndicator type="in-progress">Running</StatusIndicator>;
            case 'failed':
                return (
                    <SpaceBetween direction="horizontal" size="xs">
                        <StatusIndicator type="error">Failed</StatusIndicator>
                        <Button
                            variant="inline-link"
                            onClick={() => handleRetryJob(jobId)}
                        >
                            Retry
                        </Button>
                    </SpaceBetween>
                );
            default:
                return <StatusIndicator type="pending">{status}</StatusIndicator>;
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
    };

    const handleDownload = (s3Url) => {
        setDownloadingUrls(prev => new Set(prev).add(s3Url));
        competitiveIntelligenceApi.getDownloadLinkWebSocket(s3Url);
    };

    const handleRetryJob = (jobId) => {
        console.log('Retrying job:', jobId);
        competitiveIntelligenceApi.retryJobWebSocket(jobId);
        // Optionally show a success message or refresh the list
        alert('Job retry initiated. The job will be reprocessed.');
    };

    return (
        <Container header={<Header variant="h1">My Research</Header>}>
            <SpaceBetween size="l">
                <Table
                    columnDefinitions={[
                        {
                            id: 'created_at',
                            header: 'Created',
                            cell: item => formatDate(item.created_at),
                            sortingField: 'created_at',
                            width: 180
                        },
                        {
                            id: 'user_prompt',
                            header: 'Prompt',
                            cell: item => (
                                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {item.user_prompt || 'N/A'}
                                </div>
                            ),
                        },
                        {
                            id: 'status',
                            header: 'Status',
                            cell: item => getStatusIndicator(item.status, item.jobId),
                            width: 150
                        },
                        {
                            id: 'progress',
                            header: 'Progress',
                            cell: item => (
                                <Box fontSize="body-s">
                                    {item.progress || 'N/A'}
                                </Box>
                            ),
                            width: 300
                        },
                        {
                            id: 'jobId',
                            header: 'Job ID',
                            cell: item => (
                                <Box fontSize="body-s" color="text-body-secondary">
                                    {item.jobId}
                                </Box>
                            ),
                            width: 300
                        },
                        {
                            id: 's3_url',
                            header: 'Report',
                            cell: item => {
                                if (!item.condensed_docx_url && !item.full_docx_url) return 'N/A';
                                return (
                                    <SpaceBetween direction="vertical" size="xs">
                                        {item.condensed_docx_url && (
                                            <Button
                                                variant="inline-link"
                                                loading={downloadingUrls.has(item.condensed_docx_url)}
                                                onClick={() => handleDownload(item.condensed_docx_url)}
                                            >
                                                View Battlecard
                                            </Button>
                                        )}
                                        {item.full_docx_url && (
                                            <Button
                                                variant="inline-link"
                                                loading={downloadingUrls.has(item.full_docx_url)}
                                                onClick={() => handleDownload(item.full_docx_url)}
                                            >
                                                View Detailed Research
                                            </Button>
                                        )}
                                    </SpaceBetween>
                                );
                            },
                            width: 180
                        }
                    ]}
                    items={jobs}
                    loading={loading}
                    loadingText="Loading research jobs..."
                    sortingDisabled={false}
                    empty={
                        <Box textAlign="center" color="inherit">
                            <b>No research jobs found</b>
                            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                                Start a new research from the Battlecard Generator.
                            </Box>
                        </Box>
                    }
                    header={
                        <Header
                            counter={jobs.length > 0 ? `(${jobs.length})` : ''}
                        >
                            Past Research Jobs
                        </Header>
                    }
                />
            </SpaceBetween>
        </Container>
    );
}
